import * as t from 'io-ts';

import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {
  makeApiType,
  makeDefaultValueType,
  makeEnumRuntimeType,
  RCalendarEventUidRef,
  RCalendarEventVersionUidRef,
  RChannelIdRef,
} from './apiTypes';
import {CalendarEventModel, serializeCalendarEvent} from './calendarEventModel';
import {parseConference, RConference, serializeConference} from './conferenceModel';
import {RParticipantResponseStatus} from './participantModel';
import {parseRecurrence, RRecurrenceDirty, serializeRecurrence} from './recurrenceModel';
import {VisualParticipantModel} from './visualParticipantModel';

/*
 * Runtime type.
 */

export enum CalendarEventVersionShowAsTypesEnum {
  BUSY = 'busy',
  FREE = 'free',
}

export enum CalendarEventVersionVisibilityTypesEnum {
  DEFAULT = 'default',
  PUBLIC = 'public',
  PRIVATE = 'private',
}

export enum CalendarReminderTypesEnum {
  POPUP = 'popup',
}

export enum CalendarEventVersionVisualStatusesEnum {
  DRAFT = 'draft',
  COMPLETE = 'complete',
}

const RCalendarEventLocation = t.readonly(
  t.intersection([
    t.type({
      displayName: t.string,
    }),
    t.partial({
      address: t.string,
    }),
  ]),
);

const RCalendarEventDateTime = t.readonly(
  t.type({
    dateTime: t.string,
    timezone: t.union([t.undefined, t.string]),
  }),
);

const RCalendarEventDate = t.readonly(
  t.type({
    date: t.string,
    timezone: t.union([t.undefined, t.string]),
  }),
);

const RCalendarEventVersionDateTime = t.type({
  isAllDay: t.literal(false),
  start: RCalendarEventDateTime,
  end: RCalendarEventDateTime,
});

const RCalendarEventVersionDate = t.type({
  isAllDay: t.literal(true),
  start: RCalendarEventDate,
  end: RCalendarEventDate,
});

export const RCalendarEventVersionStartEnd = t.taggedUnion('isAllDay', [
  RCalendarEventVersionDateTime,
  RCalendarEventVersionDate,
]);

const RCalendarEventVersionVisibility = makeEnumRuntimeType(CalendarEventVersionVisibilityTypesEnum);

const RCalendarEventVersionResponse = t.readonly(
  t.type({
    status: RParticipantResponseStatus,
    address: t.string,
    comment: t.union([t.undefined, t.string]),
  }),
);

export const RCalendarEventVersionReminder = t.readonly(
  t.type({
    type: makeEnumRuntimeType(CalendarReminderTypesEnum),
    minutesBefore: t.Integer,
  }),
);

const RBaseCalendarEventVersion = t.type({
  uid: t.string,
  calendarEventUid: RCalendarEventUidRef,
  channelId: t.union([RChannelIdRef, t.undefined]),
  calendarVersionIds: t.readonlyArray(t.Integer),
  summary: t.union([t.string, t.undefined]),
  description: t.union([t.string, t.undefined]),
  locations: t.readonlyArray(RCalendarEventLocation),
  conference: makeDefaultValueType(t.union([RConference, t.undefined]), undefined),
  color: t.union([makeEnumRuntimeType(PaletteColorsEnum), t.undefined, t.null]),
  showAs: makeEnumRuntimeType(CalendarEventVersionShowAsTypesEnum),
  visibility: RCalendarEventVersionVisibility,
  recurrence: makeDefaultValueType(t.union([RRecurrenceDirty, t.undefined]), undefined),
  ical_recurrence: t.union([t.readonlyArray(t.string), t.undefined]),
  // If reminders are undefined, the calendarEventVersion inherits the default reminders of its calendar.
  reminders: t.union([t.readonlyArray(RCalendarEventVersionReminder), t.undefined]),
  includesTeammateReschedulingLink: t.boolean,
  response: t.union([RCalendarEventVersionResponse, t.undefined]),
  isRemoved: t.boolean,
  isSaving: t.boolean,
  isOrganizerCopy: t.boolean,
  calendarEventVersionUrl: t.string,
  updatedAt: t.Integer,
});

const RBaseDraftCalendarEventVersion = t.intersection([
  RBaseCalendarEventVersion,
  t.type({
    visualStatus: t.literal(CalendarEventVersionVisualStatusesEnum.DRAFT),
  }),
]);

const RBaseCompleteCalendarEventVersion = t.intersection([
  RBaseCalendarEventVersion,
  t.type({
    visualStatus: t.literal(CalendarEventVersionVisualStatusesEnum.COMPLETE),
    id: t.Integer,
  }),
]);

const RCalendarEventVersionDirty = t.readonly(
  t.intersection([
    t.taggedUnion('visualStatus', [RBaseDraftCalendarEventVersion, RBaseCompleteCalendarEventVersion]),
    RCalendarEventVersionStartEnd,
  ]),
  'calendarEventVersion',
);

/*
 * Type.
 */

export interface CalendarEventVersionLocation extends t.TypeOf<typeof RCalendarEventLocation> {}

interface CalendarEventVersionStartEndDateTime extends t.TypeOf<typeof RCalendarEventVersionDateTime> {}
interface CalendarEventVersionStartEndDate extends t.TypeOf<typeof RCalendarEventVersionDate> {}
export type CalendarEventVersionStartEnd =
  | CalendarEventVersionStartEndDateTime
  | CalendarEventVersionStartEndDate;

export interface CalendarEventVersionReminder extends t.TypeOf<typeof RCalendarEventVersionReminder> {}
export interface CalendarEventVersionResponse extends t.TypeOf<typeof RCalendarEventVersionResponse> {}

interface CalendarEventVersionDraftBase extends t.TypeOf<typeof RBaseDraftCalendarEventVersion> {}
interface CalendarEventVersionCompleteBase extends t.TypeOf<typeof RBaseCompleteCalendarEventVersion> {}

export type DraftCalendarEventVersionModel = CalendarEventVersionDraftBase & CalendarEventVersionStartEnd;
export type CompleteCalendarEventVersionModel = CalendarEventVersionCompleteBase &
  CalendarEventVersionStartEnd;

export type CalendarEventVersionModel = CompleteCalendarEventVersionModel | DraftCalendarEventVersionModel;
export const RCalendarEventVersion = t.clean<CalendarEventVersionModel>(RCalendarEventVersionDirty as any);

RCalendarEventVersionUidRef.setTargetType(RCalendarEventVersion);

/*
 * IO methods.
 */

export function parseCalendarEventVersion(src: any): CalendarEventVersionModel {
  return {
    id: src.id,
    uid: src.uid,
    calendarEventUid: src.calendar_event.uid,
    channelId: src.channel_id || undefined,
    calendarVersionIds: src.calendar_version_id ? [src.calendar_version_id] : [],
    summary: src.summary || undefined,
    description: src.description || undefined,
    locations: (src.location && src.location.map(parseLocation)) || [],
    conference: (src.conference && parseConference(src.conference)) || undefined,
    color: src.color || undefined,
    showAs: src.show_as || CalendarEventVersionShowAsTypesEnum.BUSY,
    visibility: src.visibility || CalendarEventVersionVisibilityTypesEnum.DEFAULT,
    recurrence: (src.recurrence && parseRecurrence(src.recurrence)) || undefined,
    ical_recurrence: src.ical_recurrence || undefined,
    reminders: (src.reminders && src.reminders.map(parseCalendarEventVersionReminder)) || undefined,
    response: (src.response && parseResponse(src.response)) || undefined,
    isRemoved: Boolean(src.removed),
    includesTeammateReschedulingLink: Boolean(src.includes_teammate_rescheduling_link),
    calendarEventVersionUrl: src.url,
    isSaving: false,
    visualStatus: CalendarEventVersionVisualStatusesEnum.COMPLETE,
    isOrganizerCopy: Boolean(src.is_organizer_copy),
    updatedAt: src.updated_at,
    ...parseCalendarEventVersionStartEnd(src),
  };
}

function parseLocation(src: any) {
  return {
    displayName: src.display_name,
    address: src.address || undefined,
  };
}

function parseCalendarEventVersionStartEnd(src: any): CalendarEventVersionStartEnd {
  // All day event.
  if (src.start.date && src.end.date) {
    return {
      isAllDay: true,
      start: parseCalendarEventVersionDate(src.start),
      end: parseCalendarEventVersionDate(src.end),
    };
  }

  // Standard event.
  return {
    isAllDay: false,
    start: parseCalendarEventVersionDateTime(src.start),
    end: parseCalendarEventVersionDateTime(src.end),
  };
}

function parseCalendarEventVersionDate(src: any) {
  return {
    date: src.date,
    timezone: src.timezone || undefined,
  };
}

function parseCalendarEventVersionDateTime(src: any) {
  return {
    dateTime: src.date_time,
    timezone: src.timezone || undefined,
  };
}

export function parseCalendarEventVersionReminder(src: any) {
  return {
    type: src.type,
    minutesBefore: src.minutes_before,
  };
}

function parseResponse(src: any) {
  return {
    status: src.status,
    address: src.address,
    comment: src.comment || undefined,
  };
}

export function serializeCalendarEventVersion(
  calendarEventVersion: CalendarEventVersionModel,
  calendarEvent: CalendarEventModel,
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
  isRsvp?: boolean,
  colorIsNop?: boolean,
) {
  const base = isRsvp ? {response: calendarEventVersion.response} : {};

  const color = colorIsNop ? {} : {color: calendarEventVersion.color};

  return {
    ...base,
    ...color,
    has_calendar_event_version_color_nop_fix: true,
    uid: calendarEventVersion.uid,
    calendar_event: serializeCalendarEvent(calendarEvent, visualParticipants),
    ...serializeCalendarEventVersionStartEnd(calendarEventVersion),
    summary: calendarEventVersion.summary,
    includes_teammate_rescheduling_link: calendarEventVersion.includesTeammateReschedulingLink,
    description: calendarEventVersion.description,
    location: calendarEventVersion.locations.map(serializeLocation),
    conference: calendarEventVersion.conference
      ? serializeConference(calendarEventVersion.conference)
      : undefined,
    show_as: calendarEventVersion.showAs,
    visibility: calendarEventVersion.visibility,
    recurrence: calendarEventVersion.recurrence
      ? serializeRecurrence(calendarEventVersion.recurrence)
      : undefined,
    ical_recurrence: calendarEventVersion.ical_recurrence || undefined,
    reminders:
      calendarEventVersion.reminders &&
      calendarEventVersion.reminders.map(serializeCalendarEventVersionReminder),
    removed: calendarEventVersion.isRemoved,
    updated_at: calendarEventVersion.updatedAt,
  };
}

function serializeCalendarEventVersionStartEnd(calendarEventVersion: CalendarEventVersionModel) {
  if (calendarEventVersion.isAllDay) {
    return {
      start: {
        date: calendarEventVersion.start.date,
        timezone: calendarEventVersion.start.timezone,
      },
      end: {
        date: calendarEventVersion.end.date,
        timezone: calendarEventVersion.end.timezone,
      },
    };
  }

  return {
    start: {
      date_time: calendarEventVersion.start.dateTime,
      timezone: calendarEventVersion.start.timezone,
    },
    end: {
      date_time: calendarEventVersion.end.dateTime,
      timezone: calendarEventVersion.end.timezone,
    },
  };
}

function serializeLocation(location: CalendarEventVersionLocation) {
  return {
    display_name: location.displayName,
    address: location.address,
  };
}

export function serializeCalendarEventVersionReminder(reminder: CalendarEventVersionReminder) {
  return {
    type: reminder.type,
    minutes_before: reminder.minutesBefore,
  };
}

/*
 * API type.
 */

export const ApiCalendarEventVersion = makeApiType(
  'calendarEventVersion',
  RCalendarEventVersion,
  parseCalendarEventVersion,
);
